import { Easing, Tween } from "@tweenjs/tween.js";
import Hammer from "hammerjs";

import { Factory } from "@/data/dao/Factory";
import { type IOfflineStorage, OfflineStorage } from "@/data/dao/offline/OfflineStorage";
import { ServiceType } from "@/data/model/Enums";
import { Format } from "@/data/util/Format";

import { logError } from "@/shared/logging/errors";
import { DomUtil } from "@/shared/ui/Helper";

import { appEvents } from "@/app/App";
import { TastierClient } from "@/app/TastierClient";
import { PaymentMethodSelector } from "@/app/elements/PaymentMethodSelector";
import { trans } from "@/app/localization/Trans";
import { SigninPopover } from "@/app/popover/SigninPopover";
import { SignupPopover } from "@/app/popover/SignupPopover";
import { windowHistoryService } from "@/app/services/windowHistoryService";
import { Helper } from "@/app/ui/Helper";
import { CancelWholeOrderPopover } from "@/app/popover/CancelOrderPopover";
import { Popup } from "@/app/ui/Popup";
import { PaymentMethodsReportContentPopover } from "@/app/popover/PaymentMethodsReportContentPopover";
import type { IPaymentMethodsReportData } from "@/data/model/PaymentMethodsReportData";

export type UserMenuOptions = {
    excludeLeaveTable?: boolean;
    element?: JQuery;
};

export class UserMenu {
    private $element: JQuery;

    private $nav?: JQuery;
    private $dim?: JQuery;

    private $avatarCircle: JQuery;
    private runningTween: any;
    private touchCtrl?: HammerManager;
    private isReleasing = false;
    private currentNavPosition?: number;
    private menuWidth = 270;
    private excludeLeaveTable = false;

    constructor(options: UserMenuOptions = {}) {
        this.excludeLeaveTable = options.excludeLeaveTable ?? false;
        this.$element = options.element ?? $('<div class="usermenu" />');
        const avatar = $('<div class="avatar"><div class="circle"></div></div>').appendTo(this.$element);
        this.$avatarCircle = avatar.find("> .circle");

        TastierClient.identityContext.onIdentityChange.add(this.onIdentityChanged, this);

        this.onIdentityChanged();

        avatar.click((e) => {
            e.preventDefault();
            e.stopPropagation();

            if (this.$nav === undefined) this.openNav();
        });
    }

    private onIdentityChanged() {
        const profile = TastierClient.identityContext.getProfile()!;
        const isAuthenticated = TastierClient.identityContext.isAuthenticated();
        const shortName = Helper.shortNameFromProfile(profile);

        if (isAuthenticated) {
            if (profile.avatar !== undefined)
                this.$avatarCircle
                    .removeClass("justicon")
                    .html('<div class="pic" style="background-image:url(\'' + profile.avatar + "');\" />");
            else
                this.$avatarCircle
                    .removeClass("justicon")
                    .html('<div class="shortname">' + Format.htmlEscape(shortName) + "</div>");
        } else {
            if (shortName !== "")
                this.$avatarCircle
                    .removeClass("justicon")
                    .html('<div class="shortname">' + Format.htmlEscape(shortName) + "</div>");
            else this.$avatarCircle.addClass("justicon").html('<i class="fas fa-user-circle"></i>');
        }
    }

    public showLogin(userEmail?: string) {
        const signinPop = new SigninPopover(userEmail);
        signinPop.onSignupClicked = () => {
            this.showSignup(signinPop.getInputEmail());
            signinPop.hide();
        };
        signinPop.show();
    }

    public showSignup(userEmail?: string) {
        const signupPop = new SignupPopover(userEmail);
        signupPop.onLoginClicked = () => {
            this.showLogin(signupPop.getInputEmail());
            signupPop.hide();
        };
        signupPop.show();
    }

    public showProfile() {
        this.closeNav();
        setTimeout(() => {
            appEvents.OnShowProfile.dispatch();
        }, 220);
    }

    public showOrderHistory() {
        this.closeNav();
        setTimeout(() => {
            appEvents.OnShowOrderHistory.dispatch();
        }, 220);
    }

    public showPaymentHistory() {
        this.closeNav();
        setTimeout(() => {
            appEvents.OnShowPaymentHistory.dispatch();
        }, 220);
    }

    public showHelp() {
        this.closeNav();
        setTimeout(() => {
            appEvents.OnShowHelp.dispatch();
        }, 220);
    }

    public async switchIdentity(): Promise<void> {
        const currentAllocation = TastierClient.context.currentAllocation;
        await TastierClient.identityContext.signOut(true);

        if (currentAllocation === undefined) {
            return;
        }

        const permalinkCode = windowHistoryService.getdirectLinkCodeFromUrlOrHistory();
        if (permalinkCode !== undefined) {
            await TastierClient.context.allocateByPermalink(permalinkCode);
        } else {
            await TastierClient.context.allocate(currentAllocation.locationId, currentAllocation.tableNumber);
        }
    }

    private openNav() {
        if (this.$nav === undefined) {
            this.$nav = $(
                '<div class="nav dragable"><div class="head"></div><div class="inner scrollable"></div></div>'
            ).appendTo(this.$element);
            this.buildNavContent(this.$nav);

            this.touchCtrl = new Hammer(this.$nav[0], {
                recognizers: [[Hammer.Pan, { direction: Hammer.DIRECTION_HORIZONTAL, threshold: 0 }]],

                domEvents: false,
            });

            this.touchCtrl.on("panstart", () => {
                if (this.runningTween !== undefined) {
                    this.runningTween.stop();
                    this.runningTween = undefined;
                }

                this.isReleasing = false;
                this.currentNavPosition = DomUtil.translatePosition(this.$nav!).x;
            });

            this.touchCtrl.on("panmove", (ev) => {
                this.moveNav(ev.deltaX);
            });

            this.touchCtrl.on("panend", () => {
                if (this.isReleasing) return;

                this.isReleasing = true;
                this.releaseNav();
            });
        }

        if (this.$dim === undefined) {
            const parentView = this.$element.parents(".view");
            this.$dim = $('<div class="navdim" />')
                .prependTo(parentView)
                .click((e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.closeNav();
                });
        }

        this.tweenTo(false, true);
        appEvents.OnSidenavVisibilityChanged.dispatch(true);
    }

    private buildNavContent($nav: JQuery) {
        const head = $nav.find("> .head");
        const inner = $nav.find("> .inner");

        $nav.find("> .signout").remove();
        const profile = TastierClient.identityContext.getProfile()!;
        const isAuthenticated = TastierClient.identityContext.isAuthenticated();

        //update dom
        if (isAuthenticated) {
            head.empty();

            const avatar = $('<div class="avatar" />')
                .appendTo(head)
                .click((e) => {
                    e.preventDefault();
                    this.showProfile();
                });

            if (profile.avatar === undefined)
                avatar
                    .addClass("green")
                    .append(
                        '<div class="shortname">' + Format.htmlEscape(Helper.shortNameFromProfile(profile)) + "</div>"
                    );
            else avatar.append('<div class="img" style="background-image:url(\'' + profile.avatar + "');\" />");

            $('<div class="name green">' + Format.htmlEscape(profile.firstName + " " + profile.lastName) + "</div>")
                .appendTo(head)
                .click((e) => {
                    e.preventDefault();
                    this.showProfile();
                });

            inner.empty();
            $(
                '<a class="entry"><div class="icon"><i class="fal fa-user"></i></div>' +
                    trans.navigation.profile +
                    "</a>"
            )
                .appendTo(inner)
                .click((e) => {
                    e.preventDefault();

                    this.showProfile();
                });

            $(
                '<a class="entry"><div class="icon"><i class="fal fa-shopping-cart"></i></div>' +
                    trans.navigation.orderHistory +
                    "</a>"
            )
                .appendTo(inner)
                .click((e) => {
                    e.preventDefault();

                    this.showOrderHistory();
                });

            $(
                '<a class="entry"><div class="icon"><i class="fal fa-money-bill-alt"></i></div>' +
                    trans.navigation.paymentHistory +
                    "</a>"
            )
                .appendTo(inner)
                .click((e) => {
                    e.preventDefault();

                    this.showPaymentHistory();
                });

            $(
                '<a class="entry"><div class="icon"><i class="fal fa-credit-card"></i></div>' +
                    trans.navigation.creditCards +
                    "</a>"
            )
                .appendTo(inner)
                .click((e) => {
                    e.preventDefault();

                    this.closeNav();
                    setTimeout(() => {
                        appEvents.OnShowCreditCards.dispatch();
                    }, 220);
                });

           if (TastierClient.identityContext.isWaiterOrAdmin()) {

            $(
                '<a class="entry"><div class="icon"><i class="fal fa-file-spreadsheet"></i></div>' +
                trans.navigation.reports +
                "</a>"
            )
                .appendTo(inner)
                .click((e) => {
                    e.preventDefault();

                    this.closeNav();
                    setTimeout(() => {

                        const popover = new PaymentMethodsReportContentPopover((data: IPaymentMethodsReportData) => {
                            appEvents.OnShowPaymentMethodReport.dispatch(data);
                            popover.hide();
                        });
                        popover.show();
                    }, 220);
                });

            }
         
            if (!this.excludeLeaveTable) {
                const leaveTableEntry = $(
                    '<a class="entry"><div class="icon"><i class="fal fa-door-open"></i></div>' +
                        trans.navigation.leaveTable +
                        "</a>"
                )
                    .appendTo(inner)
                    .click(async (e) => {
                        e.preventDefault();
                        this.setEntryLoading(leaveTableEntry, true);
                        try {
                            await TastierClient.context.deallocate();
                            this.closeNav();
                        } catch (ex) {
                            logError(ex);
                        } finally {
                            this.setEntryLoading(leaveTableEntry, false);
                        }
                    });
            }

            $(
                '<a class="entry"><div class="icon"><i class="fal fa-hands-helping"></i></div>' +
                    trans.navigation.help +
                    "</a>"
            )
                .appendTo(inner)
                .click((e) => {
                    e.preventDefault();
                    this.showHelp();
                });

            const signoutEntry = $(
                '<a class="signout entry"><div class="icon"><i class="fal fa-sign-out"></i></div>' +
                    trans.navigation.logout +
                    "</a>"
            )
                .appendTo($nav)
                .click(async (e) => {
                    e.preventDefault();

                    const offlineCache = Factory.getInstance<IOfflineStorage>(OfflineStorage);
                    offlineCache.remove(PaymentMethodSelector.pmtMethodCacheKey);

                    this.setEntryLoading(signoutEntry, true);
                    try {
                        await TastierClient.identityContext.signOut();
                        this.setEntryLoading(signoutEntry, false);
                        this.closeNav();
                    } catch (ex) {
                        logError(ex);
                        this.setEntryLoading(signoutEntry, false);
                    }
                });
        } else {
            head.empty();
            const shortName = Helper.shortNameFromProfile(profile);
            if (shortName !== "") {
                head.append(
                    '<div class="avatar green"><div class="shortname">' + Format.htmlEscape(shortName) + "</div></div>"
                );
                head.append(
                    '<div class="name green">' +
                        Format.htmlEscape(profile.firstName + " " + profile.lastName) +
                        "</div>"
                );
            } else {
                head.append('<div class="avataricon"><i class="fas fa-user-circle"></i></div>');
                head.append('<div class="name">' + trans.navigation.guest + "</div>");
            }

            inner.empty();

            $(
                '<a class="entry"><div class="icon"><i class="fal fa-user-plus"></i></div>' +
                    trans.navigation.signup +
                    "</a>"
            )
                .appendTo(inner)
                .click((e) => {
                    e.preventDefault();

                    this.closeNav();
                    this.showSignup();
                });

            $(
                '<a class="entry"><div class="icon"><i class="fal fa-sign-in"></i></div>' +
                    trans.navigation.login +
                    "</a>"
            )
                .appendTo(inner)
                .click((e) => {
                    e.preventDefault();
                    this.showLogin();
                    this.closeNav();
                });

            $(
                '<a class="entry"><div class="icon"><i class="fal fa-shopping-cart"></i></div>' +
                    trans.navigation.orderHistory +
                    "</a>"
            )
                .appendTo(inner)
                .click((e) => {
                    e.preventDefault();

                    this.showOrderHistory();
                });

            $(
                '<a class="entry"><div class="icon"><i class="fal fa-money-bill-alt"></i></div>' +
                    trans.navigation.paymentHistory +
                    "</a>"
            )
                .appendTo(inner)
                .click((e) => {
                    e.preventDefault();

                    this.showPaymentHistory();
                });

            if (!this.excludeLeaveTable) {
                const leaveTableEntry = $(
                    '<a class="entry"><div class="icon"><i class="fal fa-door-open"></i></div>' +
                        trans.navigation.leaveTable +
                        "</a>"
                )
                    .appendTo(inner)
                    .click(async (e) => {
                        e.preventDefault();
                        this.setEntryLoading(leaveTableEntry, true);
                        try {
                            await TastierClient.context.deallocate();
                            this.closeNav();
                        } catch (ex) {
                            logError(ex);
                        } finally {
                            this.setEntryLoading(leaveTableEntry, false);
                        }
                    });
            }

            if (
                Helper.hasService(ServiceType.Covid) &&
                Helper.getCovidServiceConfigValue<boolean>("showIdSwitch") === true &&
                Helper.getCovidServiceConfigValue<boolean>("verifyCovidCert") !== true
            ) {
                //check profile
                if (profile !== undefined && profile.phoneNumber !== undefined && profile.zip !== undefined) {
                    const switchIdentityEntry = $(
                        '<a class="entry"><div class="icon"><i class="fal fa-people-arrows"></i></div>' +
                            trans.navigation.switchIdentity +
                            "</a>"
                    )
                        .appendTo(inner)
                        .click(async (e) => {
                            e.preventDefault();

                            this.closeNav();

                            try {
                                await this.switchIdentity();
                            } catch (ex) {
                                logError(ex);
                            } finally {
                                this.setEntryLoading(switchIdentityEntry, false);
                            }
                        });
                }
            }

            $(
                '<a class="entry"><div class="icon"><i class="fal fa-hands-helping"></i></div>' +
                    trans.navigation.help +
                    "</a>"
            )
                .appendTo(inner)
                .click((e) => {
                    e.preventDefault();
                    this.showHelp();
                });
        }
    }

    public closeNav() {
        if (this.$nav === undefined) return;

        this.tweenTo(true, false);
        appEvents.OnSidenavVisibilityChanged.dispatch(false);
    }

    private onNavClosed() {
        this.$dim!.remove();
        this.$nav!.remove();
        this.$nav = undefined;
        this.$dim = undefined;
        this.touchCtrl!.destroy();
        this.touchCtrl = undefined;
    }

    private moveNav(byPixel: number) {
        let newPosition = this.currentNavPosition! + byPixel;
        let overdraging = 0;
        if (newPosition < 0) overdraging = newPosition;

        let p = 1 - (1 / this.menuWidth) * newPosition;
        if (p > 1) p = 1;
        else if (p < 0) p = 0;

        newPosition += DomUtil.momentumResistance(overdraging, 40);
        this.$dim!.css("opacity", p);
        this.$nav!.css({ transform: "translate3d(" + newPosition + "px, 0, 0)" });
    }

    private releaseNav() {
        const currentPos = DomUtil.translatePosition(this.$nav!).x;
        this.tweenTo(currentPos > 50, false);
    }

    private tweenTo(hidden: boolean, useBounce: boolean) {
        const current = {
            pos: DomUtil.translatePosition(this.$nav!).x,
            opacity: this.$dim!.css("opacity"),
        };

        const dest = {
            pos: hidden ? this.menuWidth : 0,
            opacity: hidden ? 0 : 1,
        };

        this.runningTween = new Tween(current)
            .to(dest, 300)
            .easing(useBounce ? Easing.Back.Out : Easing.Exponential.Out)
            .onUpdate((f) => {
                this.$dim!.css("opacity", f.opacity);
                this.$nav!.css({ transform: "translate3d(" + f.pos + "px, 0, 0)" });
            })
            .start();

        this.runningTween.onComplete(() => {
            if (hidden) this.onNavClosed();

            this.isReleasing = false;
            this.runningTween = undefined;
        });

        const animate = (time?: null) => {
            if (this.runningTween !== undefined && this.runningTween.isPlaying()) {
                this.runningTween.update(time);
                requestAnimationFrame(animate as any);
            }
        };

        animate();
    }

    private setEntryLoading($entry: JQuery, isLoading: boolean) {
        const parent = $entry.find("> .icon");
        parent.find("> .loader").remove();

        if (isLoading) {
            parent.append('<div class="loader" />');
            parent.find("> .fal").hide();
        } else parent.find("> .fal").show();
    }

    public appendTo($element: JQuery): UserMenu {
        this.$element.appendTo($element);
        return this;
    }
}
